import { Component, effect, EventEmitter, inject, Input, input, Output, signal } from "@angular/core";
import { ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import {
  AttachmentSelectorComponent,
  AttachmentSelectorProducts,
} from "@components/attachment-selector/attachment-selector.component";
import {
  FgdsProductType,
  isRequiredAttachment,
  LocalizeProductNamePipe,
  Product,
  RelationshipSubType,
} from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { ProductWithQuantity } from "@project/gas-warning-center-configuration/product-with-quantity";
import { map, Observable, of } from "rxjs";
import { take } from "rxjs/operators";

interface AttachmentSelectorRegard7000Products {
  selectedProducts: Product[];
  preselectedAttachments?: ProductWithQuantity[];
  preselectedSlotCovers?: ProductWithQuantity;
}

@Component({
  selector: "app-attachment-selector-regard-7000",
  templateUrl: "./attachment-selector-regard-7000.component.html",
  styleUrls: ["./attachment-selector-regard-7000.component.scss"],
  imports: [UiKitModule, ReactiveFormsModule, AttachmentSelectorComponent],
  providers: [LocalizeProductNamePipe],
  standalone: true,
})
export class AttachmentSelectorRegard7000Component {
  private productService = inject(ProductService);

  readonly products = input.required<AttachmentSelectorRegard7000Products>();
  @Input() showCosts = false;

  @Output() attachmentsSelect$ = new EventEmitter<ProductWithQuantity[]>();

  protected readonly productsForAttachmentSelector = signal<AttachmentSelectorProducts>({ selectedProducts: [] });

  constructor() {
    effect(() => {
      const products = this.products();
      if (!products.preselectedSlotCovers?.quantity) {
        this.productsForAttachmentSelector.set({
          selectedProducts: products.selectedProducts,
          preselectedAttachments: products.preselectedAttachments,
        });
        return;
      }
      if (products.preselectedAttachments) {
        const regard7000SlotCoverId = "8323812";
        const preselectedAttachments = products.preselectedAttachments.filter(
          (attachment) => attachment.product.id !== regard7000SlotCoverId,
        );
        preselectedAttachments.push(products.preselectedSlotCovers);
        this.productsForAttachmentSelector.set({
          selectedProducts: products.selectedProducts,
          preselectedAttachments,
        });
        return;
      }
      this.getRequiredAttachments(products.selectedProducts).subscribe((reqAttachments) => {
        this.productsForAttachmentSelector.set({
          selectedProducts: products.selectedProducts,
          preselectedAttachments: reqAttachments
            .map((product) => ({ product, quantity: 1 }))
            .concat([products.preselectedSlotCovers!]),
        });
      });
    });
  }

  private getRequiredAttachments(selectedProducts: Product[]): Observable<Product[]> {
    const reqIds = [
      ...new Set(
        selectedProducts
          .flatMap((product) => product.relationships)
          .filter(
            (relationship) =>
              isRequiredAttachment(relationship) &&
              relationship.relationshipSubType !== RelationshipSubType.GATEWAY &&
              relationship.relationshipSubType !== RelationshipSubType.INPUT &&
              relationship.relationshipSubType !== RelationshipSubType.RELAY &&
              relationship.relationshipSubType !== RelationshipSubType.SLOT_COVER,
          )
          .map((attachment) => attachment.productId),
      ),
    ];
    if (!reqIds.length) {
      return of([]);
    }
    return this.productService.getProductsByIds(reqIds, FgdsProductType.ATTACHMENT).pipe(
      take(1),
      map((products) => products.filter((product) => product.isAvailable)),
    );
  }
}
